// hooks
import useAlertMessageAction from '../stores/appAlertMessage/appAlertMessageAction'
import useTempFileService, { FileUploaded } from '../services/web/useTempFileService'
import useTranslate from '../i18n/useTranslate'

const DEFAULT_ACCEPT = '.png,.jpeg,.tiff,.pdf,.doc,.docx'
const DEFAULT_MAX_FILE_SIZE = 31457280 // 30 MB

export interface UploadTempFileOption {
    /** Allowed file extensions (comma-separated). Default: '.png,.jpeg,.tiff,.pdf,.doc,.docx' */
    accept?: string
    /** Maximum allowed file size in bytes. Default: 30MB (31457280) */
    maxFileSize?: number
}

/**
 * Custom React hook for validating and uploading temporary files.
 *
 * This hook handles:
 * - File extension and size validation based on configurable options.
 * - Showing localized alert messages when validation fails.
 * - Uploading files via the temporary file service.
 *
 * Example usage:
 * ```tsx
 * const { validate, upload } = useTempFile({ maxFileSize: 5 * 1024 * 1024 }) // 5MB limit
 *
 * const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0]
 *   if (file && validate(file)) {
 *     const uploaded = await upload(file)
 *     console.log('Uploaded file:', uploaded)
 *   }
 * }
 * ```
 *
 * @param {UploadTempFileOption} [option] - Configuration for accepted extensions and max file size.
 * @returns {object} An object containing:
 * - `validate`: `(file: File) => boolean` — Validates file extension and size, shows alerts if invalid.
 * - `upload`: `(file: File) => Promise<FileUploaded | null>` — Uploads a validated file and returns the uploaded file data.
 */
const useTempFile = (option?: UploadTempFileOption) => {
    const { accept = DEFAULT_ACCEPT, maxFileSize = DEFAULT_MAX_FILE_SIZE } = option ?? {}
    const tempFileServices = useTempFileService()
    const translate = useTranslate()
    const alertMessageAction = useAlertMessageAction()

    const isValidExtension = (fileName: string): boolean => {
        const allowed = accept.split(',').map((ext) => ext.trim().replace('.', '').toLowerCase())
        const parts = fileName.split('.')
        if (parts.length < 2) return false
        const ext = parts.pop()?.toLowerCase()
        return !!ext && allowed.includes(ext)
    }

    const validate = (file: File): boolean => {
        if (!isValidExtension(file.name)) {
            alertMessageAction.openAlertMessage({
                title: translate.get('ALERT.TITLE.WARNING'),
                message: translate.get('ALERT.CONTENT.UPLOAD_INVALID_FILE_EXTENSION'),
            })
            return false
        }

        if (file.size > maxFileSize) {
            alertMessageAction.openAlertMessage({
                title: translate.get('ALERT.TITLE.WARNING'),
                message: translate.get('LABEL.UPLOAD_NOT_EXCEED_30_MB'),
            })
            return false
        }

        return true
    }

    const upload = async (file: File): Promise<FileUploaded | null> => {
        try {
            const formData = new FormData()
            formData.append('files', file)
            const { result } = await tempFileServices.uploadTempFile(formData)
            return result[0]
        } catch (error) {
            console.error(error)
            return null
        }
    }

    return { validate, upload }
}

export default useTempFile
